import Box from "@mui/material/Box";
import { useState } from 'react';
import useMediaQuery from '@mui/material/useMediaQuery';
import ButtonBase from "@mui/material/ButtonBase";
import Typography from "@mui/material/Typography";
import Tooltip from "@mui/material/Tooltip";
import { 
  Compass, 
  Tv, 
  BookOpen, 
  Clapperboard, 
  Film, 
  Radio,
  Bookmark, 
  History, 
  Blocks, 
  Settings, 
  PanelLeftClose, 
  PanelLeftOpen 
} from 'lucide-react';
import { useDynamicSections } from '../../plugins/PluginManager.ts';
import { preloadView } from '../preloadView.ts';

interface Props {
  currentView: string;
  onNavigate: (view: string) => void;
}

interface NavItem {
  id: string;
  label: string;
  icon: any;
}

const mainNav: NavItem[] = [
  { id: "home", label: "Explorar", icon: Compass },
  { id: "anime", label: "Anime", icon: Tv },
  { id: "explore", label: "Manga", icon: BookOpen },
  { id: "section:series", label: "Series", icon: Clapperboard },
  { id: "section:movies", label: "Películas", icon: Film },
  { id: "live", label: "En vivo", icon: Radio },
  { id: "history", label: "Historial", icon: History },
  { id: "library", label: "Biblioteca", icon: Bookmark },
];

export function YomiriSidebar({ currentView, onNavigate }: Props) {
  const [userExpanded, setUserExpanded] = useState(() => localStorage.getItem('yomiri-sidebar-expanded') !== 'false');
  const isNarrow = useMediaQuery('(max-width: 1099px)');
  const expanded = userExpanded && !isNarrow;

  const dynamicSections = useDynamicSections();
  const dynamicNavItems: NavItem[] = dynamicSections
    .filter(section => !['manga', 'explore', 'anime', 'live', 'tv', 'series', 'movies', 'movie'].includes(section.id.toLowerCase()))
    .map(section => ({
      id: "section:" + section.id,
      label: section.title,
      icon: Clapperboard,
    }));

  const allNavItems = [...mainNav, ...dynamicNavItems];

  const bottomNav: NavItem[] = [
    { id: "sources", label: "Fuentes", icon: Blocks },
    { id: "more", label: "Ajustes", icon: Settings },
  ];

  const renderButton = (item: NavItem) => {
    const active =
      currentView === item.id ||
      (item.id === 'home' && currentView === '') ||
      (item.id === 'library' && currentView === 'downloads') ||
      (item.id === 'more' && ['appearance', 'about'].includes(currentView));

    const Icon = item.icon;

    const btn = (
      <ButtonBase
        key={item.id}
        type="button"
        onClick={() => onNavigate(item.id)}
        onPointerEnter={() => preloadView(item.id)}
        onFocus={() => preloadView(item.id)}
        aria-current={active ? "page" : undefined}
        aria-label={item.label}
        sx={{
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          justifyContent: expanded ? "flex-start" : "center",
          position: "relative",
          gap: 1.5,
          width: "100%",
          height: 38,
          minHeight: 38,
          px: expanded ? 1.5 : 0,
          border: 0,
          borderRadius: "6px",
          color: active ? "#ffffff" : "#94a3b8",
          backgroundColor: active ? "#13131c" : "transparent",
          textAlign: "left",
          cursor: "pointer",
          font: "inherit",
          transition: "all .15s ease",
          "&:hover": {
            backgroundColor: active ? "#1a1a26" : "rgba(255, 255, 255, 0.04)",
            color: "#ffffff",
          },
          "&:focus-visible": {
            outline: "2px solid var(--accent-500, #ff2e63)",
            outlineOffset: 2,
          },
        }}
      >
        {active && (
          <Box
            sx={{
              position: "absolute",
              left: 0,
              top: 7,
              bottom: 7,
              width: 3,
              borderRadius: "0 2px 2px 0",
              backgroundColor: "var(--accent-500, #ff2e63)",
              boxShadow: "0 0 8px rgba(255, 46, 99, 0.7)",
            }}
          />
        )}

        <Box
          sx={{
            display: "grid",
            placeItems: "center",
            width: 20,
            height: 20,
            color: active ? "var(--accent-500, #ff2e63)" : "inherit",
            flexShrink: 0,
          }}
        >
          <Icon size={18} strokeWidth={active ? 2.2 : 1.75} />
        </Box>

        {expanded && (
          <Typography
            component="span"
            sx={{
              fontSize: 13,
              letterSpacing: "-0.01em",
              fontWeight: active ? 600 : 500,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {item.label}
          </Typography>
        )}
      </ButtonBase>
    );

    if (!expanded) {
      return (
        <Tooltip key={item.id} title={item.label} placement="right" arrow>
          {btn}
        </Tooltip>
      );
    }

    return btn;
  };

  return (
    <Box
      component="nav"
      aria-label="Navegación principal de Yomiri"
      sx={{
        width: expanded ? 210 : 58,
        transition: 'width .2s ease',
        flexShrink: 0,
        height: "100%",
        position: "relative",
        zIndex: 1200,
        px: 0.75,
        pt: 1.25,
        pb: 1.25,
        backgroundColor: "#000000",
        borderRight: "1px solid rgba(255, 255, 255, 0.07)",
        display: { xs: "none", md: "flex" },
        flexDirection: "column",
        justifyContent: "space-between",
      }}
    >
      {/* Top Main Navigation */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 0.5,
          width: "100%",
          overflowY: "auto",
          scrollbarWidth: "none",
          "&::-webkit-scrollbar": { display: "none" },
        }}
      >
        {allNavItems.map(renderButton)}
      </Box>

      {/* Bottom Actions: Fuentes, Ajustes & Compact Toggle */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 0.5,
          pt: 1,
          borderTop: "1px solid rgba(255, 255, 255, 0.06)",
        }}
      >
        {bottomNav.map(renderButton)}

        {/* Compact Toggle Button */}
        {!isNarrow && (
          <Tooltip title={expanded ? "Contraer barra" : "Expandir barra"} placement="right" disableHoverListener={expanded}>
            <ButtonBase
              aria-label={expanded ? "Contraer menú" : "Expandir menú"}
              aria-expanded={expanded}
              onClick={() =>
                setUserExpanded((value) => {
                  localStorage.setItem('yomiri-sidebar-expanded', String(!value));
                  return !value;
                })
              }
              sx={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                justifyContent: expanded ? "flex-start" : "center",
                gap: 1.5,
                width: "100%",
                height: 32,
                minHeight: 32,
                px: expanded ? 1.5 : 0,
                borderRadius: "6px",
                color: "#64748b",
                fontSize: 12,
                transition: "all .15s ease",
                "&:hover": {
                  bgcolor: "rgba(255, 255, 255, 0.04)",
                  color: "#94a3b8",
                },
                "&:focus-visible": {
                  outline: "2px solid var(--accent-500, #ff2e63)",
                  outlineOffset: 2,
                },
              }}
            >
              <Box sx={{ display: "grid", placeItems: "center", width: 20, height: 20, flexShrink: 0 }}>
                {expanded ? <PanelLeftClose size={16} strokeWidth={1.8} /> : <PanelLeftOpen size={16} strokeWidth={1.8} />}
              </Box>
              {expanded && (
                <Typography component="span" sx={{ fontSize: 12, fontWeight: 500, color: 'inherit' }}>
                  Contraer
                </Typography>
              )}
            </ButtonBase>
          </Tooltip>
        )}
      </Box>
    </Box>
  );
}
